import { useCallback, useEffect, useRef } from 'react';
import type { ReactElement } from 'react';

type Politeness = 'polite' | 'assertive';

interface LiveRegionSingleton {
  polite: HTMLDivElement;
  assertive: HTMLDivElement;
  refs: number;
}

let singleton: LiveRegionSingleton | null = null;

function ensureSingleton(): LiveRegionSingleton {
  if (singleton) return singleton;
  if (typeof document === 'undefined') {
    // SSR fallback; callers should guard announce() too.
    throw new Error('useLiveRegion requires a DOM');
  }

  const makeRegion = (politeness: Politeness, id: string): HTMLDivElement => {
    let el = document.getElementById(id) as HTMLDivElement | null;
    if (el) return el;
    el = document.createElement('div');
    el.id = id;
    el.setAttribute('aria-live', politeness);
    el.setAttribute('aria-atomic', 'true');
    el.setAttribute('role', politeness === 'assertive' ? 'alert' : 'status');
    // Visually hidden, but available to assistive tech.
    el.style.position = 'absolute';
    el.style.width = '1px';
    el.style.height = '1px';
    el.style.padding = '0';
    el.style.margin = '-1px';
    el.style.overflow = 'hidden';
    el.style.clip = 'rect(0, 0, 0, 0)';
    el.style.whiteSpace = 'nowrap';
    el.style.border = '0';
    document.body.appendChild(el);
    return el;
  };

  singleton = {
    polite: makeRegion('polite', 'hsy-live-region-polite'),
    assertive: makeRegion('assertive', 'hsy-live-region-assertive'),
    refs: 0,
  };
  return singleton;
}

/**
 * Shared aria-live region for announcing XP earned, coin earned, level-ups, etc.
 * The returned `LiveRegion` component is a no-op placeholder — the DOM nodes are
 * managed as a singleton so announcements work even without mounting it.
 */
export function useLiveRegion(): {
  announce: (message: string, politeness?: Politeness) => void;
  LiveRegion: () => ReactElement | null;
} {
  const mountedRef = useRef(false);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const s = ensureSingleton();
    s.refs += 1;
    mountedRef.current = true;
    return () => {
      if (singleton) {
        singleton.refs = Math.max(0, singleton.refs - 1);
      }
    };
  }, []);

  const announce = useCallback((message: string, politeness: Politeness = 'polite') => {
    if (typeof document === 'undefined') return;
    const s = ensureSingleton();
    const target = politeness === 'assertive' ? s.assertive : s.polite;
    // Clear then set so repeated identical messages still fire.
    target.textContent = '';
    window.setTimeout(() => {
      target.textContent = message;
    }, 30);
  }, []);

  const LiveRegion = useCallback((): ReactElement | null => null, []);

  return { announce, LiveRegion };
}
